import { useContext, useEffect, useRef } from 'react';
import { UserContext } from '../UserContext.jsx';

const HEARTBEAT_MS = 30000;

/**
 * Logs one login per browser session, then heartbeats presence while the tab is visible.
 */
export const usePresenceHeartbeat = () => {
	const { userInfo, apiCall } = useContext(UserContext);
	const loggedRef = useRef(false);

	useEffect(() => {
		if (!userInfo?.email || !apiCall) return undefined;

		const payload = {
			email: userInfo.email,
			personalInfo: {
				firstName: userInfo.personalInfo?.firstName || '',
				lastName: userInfo.personalInfo?.lastName || '',
				title: userInfo.personalInfo?.title || '',
			},
		};

		const sessionKey = `find-login-logged:${userInfo.email.toLowerCase()}`;
		let intervalId;

		const sendLogin = async () => {
			if (loggedRef.current) return;
			if (sessionStorage.getItem(sessionKey) === '1') {
				loggedRef.current = true;
				return;
			}
			try {
				await apiCall('addlogin', payload, 'POST');
				sessionStorage.setItem(sessionKey, '1');
				loggedRef.current = true;
			}
			catch (err) {
				console.error('Failed to record Find login', err);
			}
		};

		const sendHeartbeat = async () => {
			if (document.visibilityState === 'hidden') return;
			try {
				await apiCall('heartbeat', payload, 'POST');
			}
			catch (err) {
				console.error('Failed to send Find heartbeat', err);
			}
		};

		const startInterval = () => {
			if (intervalId) return;
			intervalId = setInterval(sendHeartbeat, HEARTBEAT_MS);
		};

		const stopInterval = () => {
			if (!intervalId) return;
			clearInterval(intervalId);
			intervalId = undefined;
		};

		const onVisibility = () => {
			if (document.visibilityState === 'visible') {
				sendHeartbeat();
				startInterval();
			}
			else {
				stopInterval();
			}
		};

		sendLogin().finally(() => {
			if (document.visibilityState === 'visible') {
				sendHeartbeat();
				startInterval();
			}
		});
		document.addEventListener('visibilitychange', onVisibility);

		return () => {
			stopInterval();
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [userInfo, apiCall]);
};
